/**
 * NNLineChart — shared canvas-based line chart using Apache ECharts.
 * Tree-shaken to only LineChart + Grid + Tooltip + Legend + Canvas renderer.
 *
 * Props:
 *   data     — array of data objects, e.g. [{ month:'Jan', nps:72, cahps:84 }]
 *   series   — array of { key, name, color } where color is a CSS hex/rgb string
 *   xKey     — key used for x-axis labels (default 'month')
 *   height   — pixel height (default 260)
 */
import * as echarts from 'echarts/core';
import { LineChart } from 'echarts/charts';
import { GridComponent, TooltipComponent, LegendComponent } from 'echarts/components';
import { CanvasRenderer } from 'echarts/renderers';
import ReactECharts from 'echarts-for-react';

echarts.use([LineChart, GridComponent, TooltipComponent, LegendComponent, CanvasRenderer]);

const PALETTE = ['#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#f97316'];

function hexToRgba(hex, alpha) {
  if (!hex || !hex.startsWith('#') || hex.length < 7) return hex || 'transparent';
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return `rgba(${r},${g},${b},${alpha})`;
}

function areaGradient(color) {
  return new echarts.graphic.LinearGradient(0, 0, 0, 1, [
    { offset: 0, color: hexToRgba(color, 0.18) },
    { offset: 1, color: hexToRgba(color, 0.00) },
  ]);
}

export default function NNLineChart({ data = [], series = [], xKey = 'month', height = 260 }) {
  const showLegend = series.length > 1;

  const option = {
    animation: true,
    animationDuration: 480,
    animationEasing: 'cubicOut',
    grid: { top: showLegend ? 36 : 12, right: 12, bottom: 32, left: 48, containLabel: false },
    legend: showLegend
      ? { top: 4, right: 0, itemWidth: 14, itemHeight: 3, textStyle: { fontSize: 11, color: '#6b7280' } }
      : { show: false },
    xAxis: {
      type: 'category',
      data: data.map(d => d[xKey]),
      boundaryGap: false,
      axisLine: { lineStyle: { color: '#e5e7eb' } },
      axisTick: { show: false },
      axisLabel: { color: '#9ca3af', fontSize: 11, margin: 8 },
    },
    yAxis: {
      type: 'value',
      splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      axisLabel: { color: '#9ca3af', fontSize: 11 },
      axisLine: { show: false },
      axisTick: { show: false },
    },
    tooltip: {
      trigger: 'axis',
      backgroundColor: '#ffffff',
      borderColor: '#e5e7eb',
      borderWidth: 1,
      padding: [8, 12],
      textStyle: { fontSize: 12, color: '#111827' },
      extraCssText: 'box-shadow:0 4px 16px rgba(0,0,0,0.10);border-radius:8px;',
    },
    series: series.map((s, i) => {
      const color = s.color || PALETTE[i % PALETTE.length];
      return {
        name: s.name,
        type: 'line',
        data: data.map(d => d[s.key]),
        smooth: 0.4,
        symbol: 'circle',
        symbolSize: 5,
        lineStyle: { width: 2, color },
        itemStyle: { color, borderWidth: 2, borderColor: '#fff' },
        areaStyle: { color: areaGradient(color) },
      };
    }),
  };

  return (
    <ReactECharts
      echarts={echarts}
      option={option}
      style={{ height, width: '100%' }}
      opts={{ renderer: 'canvas' }}
    />
  );
}
